<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditReader.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditReader.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditReader.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form id="componentForm" name="componentForm" class="pb-4" @submit.prevent="handleSubmit">
                <div class="row">
                    <div class="col-xxl-8 col-xl-7 d-flex flex-column">
                        <!-- Infos générales -->
                        <div class="card flex-grow-1 mb-3">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.generalConfig') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row align-items-end">
                                    <div class="mb-2 col-md-6">
                                        <label for="nom" class="form-label">{{ $t('componentEditReader.readerName') }}</label>
                                        <input type="text" class="form-control" id="nom" v-model="reader.label" required maxlength="32" :placeholder="$t('componentEditReader.readerName')" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="nom-porte" class="form-label">{{ $t('componentEditReader.doorName') }}</label>
                                        <input v-if="reader.one_door_mode && !reader.is_fusion_reader" type="text" class="form-control" :value="$t('componentEditReader.seeLecteur', { n: reader.position - 1 })" disabled/>
                                        <input v-else type="text" class="form-control" id="nom-porte" v-model="reader.door_label" required maxlength="32" :placeholder="$t('componentEditReader.doorName')" />
                                    </div>
                                    <div v-if="!reader.is_fusion_reader" class="mb-2 col-md-6">
                                        <label for="anti-pass-back" class="form-label">{{ $t('common.antiPassBack') }}</label>
                                        <select class="form-select" id="anti-pass-back" v-model="reader.anti_pass_back">
                                            <option value="Neutral">{{ $t('componentEditReader.antiPassBack.neutral') }}</option>
                                            <option value="Entry">{{ $t('componentEditReader.antiPassBack.entry') }}</option>
                                            <option value="Exit">{{ $t('componentEditReader.antiPassBack.exit') }}</option>
                                        </select>
                                    </div>
                                    <div v-if="reader.position % 2 === 0 && !reader.is_meca_reader && !reader.is_fusion_reader" class="mb-2 col-md-6">
                                        <label for="anti-pass-back" class="form-label">{{ $t('componentEditReader.readers', { a: reader.position - 1, b: reader.position }) }}</label>
                                        <select class="form-select" id="anti-pass-back" v-model="reader.one_door_mode">
                                            <option :value="true">{{ $t('componentEditReader.sameDoor') }}</option>
                                            <option :value="false">{{ $t('componentEditReader.differentDoors') }}</option>
                                        </select>
                                    </div>
                                    <div v-if="reader.anti_pass_back != 'Neutral' && !reader.is_fusion_reader" class="mb-2 col-md-6">
                                        <label for="group-counter-active" class="form-label">{{ $t('componentEditReader.groupCounter') }}</label>
                                        <select class="form-select" id="group-counter-active" v-model="reader.group_counter_active">
                                            <option :value="false">{{ $t('common.no') }}</option>
                                            <option :value="true">{{ $t('common.yes') }}</option>
                                        </select>
                                    </div>
                                    <div v-if="available_lprs && !reader.is_meca_reader && !reader.is_fusion_reader" class="mb-2 col-md-6">
                                        <label for="lpr-input" class="form-label">{{ $t('componentEditReader.associatedLPR') }}</label>
                                        <select class="form-select" id="lpr-input" v-model="reader.lpr_source_id">
                                            <option :value="0">{{ $t('componentEditReader.noLPR') }}</option>
                                            <option v-for="lpr in available_lprs" :key="lpr.id" :value="lpr.id">
                                                {{ lpr.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="reader.is_meca_reader">
                            <!-- Nombre de bits -->
                            <div class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.badgeFormat') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="input-group">
                                        <span class="input-group-text">{{ $t('componentEditReader.bitCount') }}</span>
                                        <input type="number" class="form-control" :min="16" :max="56" v-model="reader.meca_config.card_size">
                                        <span class="input-group-text">bits</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Infos ouverture -->
                            <div class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.openingConfig') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="mb-2 col-md-6">
                                            <label for="mode-ouverture" class="form-label">{{ $t('componentEditReader.openingMode') }}</label>
                                            <select class="form-select" id="mode-ouverture" v-model="reader.getOpeningDelay">
                                                <option value="0">{{ $t('componentEditReader.openingModes.openClose') }}</option>
                                                <option value="1">{{ $t('componentEditReader.openingModes.timed') }}</option>
                                            </select>
                                        </div>
                                        <div v-if="reader.getOpeningDelay > 0" id="temporisation-settings">
                                            <TempoWithUnit :label="$t('common.delay')"
                                                v-model:value="reader.opening_delay.value"
                                                v-model:unit="reader.opening_delay.unit"
                                                :min="1"
                                                :max="255">
                                            </TempoWithUnit>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Liste blanche -->
                            <div id="meca-white-list" class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                    <h4 class="header-title">{{ $t('componentEditReader.whiteList') }}</h4>
                                    <button id="meca-add-white-card" type="button" class="btn btn-sm btn-outline-primary" @click="addWhiteCard">
                                        <i class="mdi mdi-plus me-1"></i>{{ $t('componentEditReader.addBadge') }}
                                    </button>
                                </div>
                                <div v-if="whiteList.length > 0" class="card-body pt-2">
                                    <table class="table table-centered mb-0">
                                        <tbody>
                                            <tr v-for="number in whiteList" :key="number">
                                                <td class="fw-bold text-primary white-card-number">{{ number }}</td>
                                                <td class="table-action text-end">
                                                    <a href="javascript:void(0);" class="text-danger white-card-delete" :title="$t('common.delete')" :aria-label="$t('common.delete')" @click="removeWhiteCard(number)">
                                                        <i class="mdi mdi-trash-can font-18"></i>
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Informations -->
                            <div id="meca-info" class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.info') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="mb-2 col-12">
                                            <label for="meca-information" class="form-label">{{ $t('componentEditReader.handleOrCylinder') }}</label>
                                            <div class="input-group">
                                                <input id="meca-information" type="text" class="form-control" :value="reader.meca_config.information" readonly />
                                                <button v-if="reader.is_aperio_reader" id="meca-change-lock" type="button" class="btn btn-light border" :disabled="changingLock"
                                                    :title="$t('common.edit')" :aria-label="$t('common.edit')" @click="changeAperioLock">
                                                    <span v-if="changingLock" class="spinner-border spinner-border-sm" role="status"></span>
                                                    <i v-else class="mdi mdi-pencil"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <template v-if="reader.is_uz_reader || reader.is_aperio_reader">
                                            <div class="mb-2 col-md-6">
                                                <label for="meca-hub-name" class="form-label">{{ $t('componentEditReader.hubName') }}</label>
                                                <input id="meca-hub-name" type="text" class="form-control" v-model="reader.meca_config.gateway_name" @input="onGatewayNameInput"
                                                    maxlength="32" :placeholder="$t('componentEditReader.hubNamePlaceholder')" autocomplete="off" required />
                                            </div>
                                            <div class="mb-2 col-md-6">
                                                <label for="meca-hub-ip" class="form-label">{{ $t('componentEditReader.ipAddress') }}</label>
                                                <input id="meca-hub-ip" type="text" class="form-control" :value="reader.meca_config.gateway_ip" @input="onGatewayIpInput"
                                                    inputmode="decimal" :placeholder="$t('componentEditReader.ipPlaceholder')" autocomplete="off" required />
                                            </div>
                                        </template>
                                        <div v-else class="mb-2 col-12">
                                            <label for="meca-hub" class="form-label">{{ $t('componentEditReader.hub') }}</label>
                                            <input id="meca-hub" type="text" class="form-control" :value="`${reader.meca_config.gateway_name} (${reader.meca_config.gateway_ip})`" readonly />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="!reader.is_fusion_reader">
                            <!-- Formats de lecteur -->
                            <div class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <div class="d-sm-flex align-items-center justify-content-between">
                                        <h4 class="header-title">{{ $t('componentEditReader.readerFormats') }}</h4>
                                        <div class="d-flex flex-wrap gap-2 mt-2 mt-sm-0">
                                            <button v-if="reader.is_rs485" type="button" class="btn btn-sm btn-outline-secondary" data-bs-toggle="modal" data-bs-target="#cardProfileListModal">{{ $t('componentEditReader.badgeFormats') }}</button>
                                            <button v-if="reader.reader_formats.length > 0" type="button" class="btn btn-sm btn-outline-primary" @click="addReaderFormat" data-bs-toggle="modal" data-bs-target="#openReaderFormatModal" :disabled="canAddReaderFormat === false">{{ $t('componentEditReader.addFormat') }}</button>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body pt-0">
                                    <table v-if="reader.reader_formats.length > 0" class="table table-centered">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('componentEditReader.formatColumn') }}</th>
                                                <th class="table-action text-end">&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(format, index) in reader.reader_formats" :key="index">
                                                <td>{{ format.toString() }}</td>
                                                <td class="table-action text-end">
                                                    <a href="javascript:void(0);" class="text-secondary" @click="editReaderFormat(format, index)" data-bs-toggle="modal" data-bs-target="#openReaderFormatModal">
                                                        <i class="mdi mdi-pencil font-18"></i>
                                                    </a>
                                                    <a href="javascript:void(0);" class="text-secondary" @click="removeReaderFormat(index)">
                                                        <i class="mdi mdi-trash-can font-18"></i>
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-if="reader.reader_formats.length === 0" class="h-100 d-flex justify-content-center align-items-center">
                                        <button type="button" class="btn btn-sm btn-outline-primary" @click="addReaderFormat" data-bs-toggle="modal" data-bs-target="#openReaderFormatModal">{{ $t('componentEditReader.addFormat') }}</button>
                                    </div>
                                </div>
                            </div>

                            <!-- Signalisation des lecteurs RS485 -->
                            <div v-if="reader.is_rs485" class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.signaling') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="row align-items-end">
                                        <div v-for="field in ledFields" :key="field.key" class="mb-3 col-md-4">
                                            <label :for="`reader-led-${field.key}`" class="form-label">{{ field.label }}</label>
                                            <div class="input-group">
                                                <button
                                                    :id="`reader-led-${field.key}`"
                                                    type="button"
                                                    class="btn color-swatch"
                                                    :title="`Modifier ${field.label}`"
                                                    :aria-label="`Modifier ${field.label}`"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#readerColorPickerModal"
                                                    @click="openColorPicker(field)"
                                                >
                                                    <span :style="{ backgroundColor: colorToHex(reader.led_config[field.key]) }"></span>
                                                </button>
                                                <span class="input-group-text">{{ colorToHex(reader.led_config[field.key]) }}</span>
                                            </div>
                                        </div>
                                        <div class="mb-3 col-md-4">
                                            <label class="form-label" for="buzzer-on-scan">{{ $t('componentEditReader.buzzerOnScan') }}</label>
                                            <select id="buzzer-on-scan" class="form-select buzzer-select" v-model="reader.buzzer_on_scan">
                                                <option value="Inactive">{{ $t('componentEditReader.buzzerLevels.inactive') }}</option>
                                                <option value="Weak">{{ $t('componentEditReader.buzzerLevels.weak') }}</option>
                                                <option value="Mean">{{ $t('componentEditReader.buzzerLevels.mean') }}</option>
                                                <option value="Strong">{{ $t('componentEditReader.buzzerLevels.strong') }}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Infos ouverture -->
                            <div class="card flex-grow-1 mb-3">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.openingConfig') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="mb-2 col-md-6">
                                            <label for="mode-ouverture" class="form-label">{{ $t('componentEditReader.openingMode') }}</label>
                                            <select class="form-select" id="mode-ouverture" v-model="reader.getOpeningDelay">
                                                <option value="0">{{ $t('componentEditReader.openingModes.openClose') }}</option>
                                                <option value="1">{{ $t('componentEditReader.openingModes.timed') }}</option>
                                            </select>
                                        </div>
                                        <div v-if="reader.getOpeningDelay > 0" id="temporisation-settings">
                                            <TempoWithUnit :label="$t('common.delay')"
                                                v-model:value="reader.opening_delay.value"
                                                v-model:unit="reader.opening_delay.unit"
                                                :min="1"
                                                :max="255">
                                            </TempoWithUnit>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Infos entrées -->
                            <div class="card flex-shrink-0">
                                <div class="card-header pb-0">
                                    <h4 class="header-title">{{ $t('componentEditReader.inputConfig') }}</h4>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="mb-2 col-12">
                                            <label for="bouton-poussoir" class="form-label">{{ $t('componentEditReader.pushButton') }}</label>
                                            <input v-if="reader.one_door_mode" type="text" class="form-control" :value="$t('componentEditReader.seeLecteur', { n: reader.position - 1 })" disabled/>
                                            <select v-else class="form-select" id="bouton-poussoir" v-model="reader.push_button_input_type">
                                                <option value="undefined">{{ $t('common.disabled') }}</option>
                                                <option value="NO">NO</option>
                                                <option value="NC">NF</option>
                                            </select>
                                        </div>
                                        <div class="mb-2 col-12">
                                            <label for="contact-porte" class="form-label">{{ $t('componentEditReader.doorContact') }}</label>
                                            <input v-if="reader.one_door_mode" type="text" class="form-control" :value="$t('componentEditReader.seeLecteur', { n: reader.position - 1 })" disabled/>
                                            <select v-else class="form-select" id="contact-porte" v-model="reader.door_contact_input_type">
                                                <option value="undefined">{{ $t('common.disabled') }}</option>
                                                <option value="NO">NO</option>
                                                <option value="NC">NF</option>
                                            </select>
                                        </div>
                                        <div v-if="reader.one_door_mode === false && reader.door_contact_input_type !== 'undefined'" class="mb-3">
                                            <TempoWithUnit :label="$t('componentEditReader.blockedDelay')"
                                                v-model:value="reader.door_blocked_delay.value"
                                                v-model:unit="reader.door_blocked_delay.unit"
                                                :min="0"
                                                :max="255"
                                                :show-unit="false"
                                                :textIfZero="$t('common.disabled')">
                                            </TempoWithUnit>
                                        </div>
                                        <div class="mb-2 col-12">
                                            <div class="row align-items-center">
                                                <ExternalInput :label="$t('componentEditReader.vehicleDetection')" icon="mdi-car-arrow-right" :inputs="inputs" v-model:inputValue="reader.vehicle_detection_input" />
                                            </div>
                                        </div>
                                        <div class="mb-2 col-12">
                                            <div class="row align-items-center">
                                                <ExternalAlarmInput :label="$t('componentEditReader.alarmState')" icon="mdi-alarm-light" :inputs="inputs" :externalAlarms="externalAlarms" v-model:inputValue="reader.alarm_state_input" />
                                            </div>
                                        </div>
                                        <div class="mb-2 col-12">
                                            <div class="row align-items-center">
                                                <ExternalInput :label="$t('componentEditReader.emergencyButton')" icon="mdi-image-broken-variant" :inputs="inputs" v-model:inputValue="reader.emergency_button_input" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Pilotage de l'alarme -->
                        <div v-if="!reader.is_meca_reader && !reader.is_fusion_reader" class="card flex-shrink-0">
                            <div class="card-header">
                                <div class="d-flex align-items-center justify-content-between">
                                    <h4 class="header-title">{{ $t('componentEditReader.alarmControl') }}</h4>
                                    <div class="form-check">
                                        <input type="checkbox" id="pilotage-alarme" v-model="reader.alarm_control.enable" data-switch="bool" />
                                        <label for="pilotage-alarme" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                    </div>
                                </div>
                            </div>
                            <div id="pilotage-settings" class="card-body pt-0" v-if="reader.alarm_control.enable">
                                <div class="row">
                                    <div class="mb-2">
                                        <label class="form-label">{{ $t('componentEditReader.activateAlarm') }}</label>
                                        <div class="d-flex align-items-center">
                                            <select class="form-select me-2" v-model="reader.alarm_control.user_repetition">
                                                <option v-for="n in 5" :key="n" :value="n">{{ $t('componentEditReader.repetition.times', { n }) }}</option>
                                            </select>
                                            <div class="flex-shrink-0 px-3">{{ $t('componentEditReader.repetition.lessThan') }}</div>
                                            <select class="form-select" v-model="reader.alarm_control.repetition_delay">
                                                <option v-for="n in 255" :key="n" :value="n">{{ $t('componentEditReader.repetition.seconds', { n }) }}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="col-12 mb-2">
                                        <div class="d-sm-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                            <label class="form-check-label flex-grow-1 mb-2 mb-sm-0 me-2" for="desactiver-alarme">{{ $t('componentEditReader.deactivateAlarmFirst') }}</label>
                                            <input type="checkbox" id="desactiver-alarme" v-model="reader.alarm_control.deactivate_at_first_accepted_user" checked data-switch="bool">
                                            <label for="desactiver-alarme" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                        </div>
                                    </div>
                                    <div class="mb-2">
                                        <label for="sortie" class="form-label">{{ externalAlarms.length > 0 ? $t('componentEditReader.controlMode') : $t('componentEditReader.output') }}</label>
                                        <div class="d-flex align-items-center">
                                            <select v-if="externalAlarms.length > 0" class="form-select me-2" v-model="reader.alarm_control.output.mode">
                                                <option :value="0">{{ $t('componentEditReader.internalRelay') }}</option>
                                                <option :value="1">{{ $t('componentEditReader.externalAlarmSystem') }}</option>
                                            </select>
                                            <select v-if="usesAlarmRelay" class="form-select" v-model="reader.alarm_control.output.output_number" required>
                                                <option disabled value="">{{ $t('componentEditReader.selectOutput') }}</option>
                                                <option v-for="output in outputs" :key="output.output_number" :value="output.output_number">
                                                    {{ output.toString() }}
                                                </option>
                                            </select>
                                            <select v-else class="form-select" v-model="reader.alarm_control.output.external_alarm_id" required>
                                                <option disabled value="">{{ $t('componentEditReader.selectAlarm') }}</option>
                                                <option v-for="alarm in externalAlarms" :key="alarm.id" :value="alarm.id">
                                                    {{ alarm.name }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                    <div v-if="usesAlarmRelay" class="mb-2">
                                        <TempoWithUnit :label="$t('common.delay')"
                                            v-model:value="reader.alarm_control.activation_delay.value" unit="seconde(s)" :min="0" :max="255"
                                            :show-unit="false"
                                            :textIfZero="$t('common.bistable')">
                                        </TempoWithUnit>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Ouverture à distance -->
                        <div v-if="!reader.is_fusion_reader" class="card flex-shrink-0">
                            <div class="card-header">
                                <div class="d-flex align-items-center justify-content-between">
                                    <h4 class="header-title">{{ $t('componentEditReader.remoteOpening') }}</h4>
                                    <div class="form-check">
                                        <input type="checkbox" id="remote-opening" v-model="reader.remote_opening" data-switch="bool" />
                                        <label for="remote-opening" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                    </div>
                                </div>
                            </div>
                            <div v-if="reader.remote_opening" class="card-body pt-0">
                                <div class="form-check" style="margin-bottom: 0.5rem;">
                                    <input type="checkbox" class="form-check-input" id="geolocalization" v-model="reader.geolocalization"/>
                                    <label class="form-check-label" for="geolocalization">{{ $t('componentEditReader.geoControl') }}</label>
                                </div>
                                <div v-if="reader.geolocalization" class="row align-items-end">
                                    <div class="mb-2 col-md-6">
                                        <label for="latitude" class="form-label">{{ $t('componentEditReader.latitude') }}</label>
                                        <input type="number" class="form-control" id="latitude" v-model="reader.gps_coordinates.latitude" required maxlength="32" min="-180" max="180" step="any"/>
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="longitude" class="form-label">{{ $t('componentEditReader.longitude') }}</label>
                                        <input type="number" class="form-control" id="longitude" v-model="reader.gps_coordinates.longitude" required maxlength="32" min="-180" max="180" step="any"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-xxl-4 col-xl-5 d-flex flex-column">
                        <!-- [IF] Alertes présentes -->
                        <div class="card flex-shrink-0 border border-danger">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditReader.alerts') }}</h4>
                                <span class="badge bg-danger px-1">0</span>
                            </div>
                            <div class="card-body">
                                <ul class="list-group mb-0">
                                </ul>
                            </div>
                        </div>
                        <!-- [end IF] Alertes présentes -->
                        <div class="card flex-grow-1">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditReader.composition') }}</h4>
                                <span v-if="hub.hub_state === 1" class="badge bg-success font-14 px-2">{{ $t('common.connected') }}</span>
                                <span v-else class="badge bg-danger font-14 px-2">{{ $t('common.disconnected') }}</span>
                            </div>
                            <div class="card-body ps-1">
                                <div id="jstree-components" ref="tree" class="component-treeview tree-components-lite">
                                    <ul>
                                        <li data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
                                            <div class="tree-row">
                                                <div class="tree-cell component-name fw-bold text-primary">{{ hub.label }}</div>
                                                <div class="tree-cell component-action">
                                                    <TreeButton :path="hub.path()" />
                                                </div>
                                            </div>
                                            <ul>
                                                <li v-for="reader in hub.readers" :key="`${hub.id}_${reader.position}`" :data-jstree="getReaderIcon(reader.position)">
                                                    <div :class="['tree-row', { active: isSelected(reader.position) }]">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ reader.label }}</div>
                                                        <div class="tree-cell component-type">{{ reader.typeLabel }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton v-if="!isSelected(reader.position)" :path="reader.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                                <li v-for="module in hub.extensions" :key="module.key(hub.id)" data-jstree='{ "icon" : "mdi mdi-key-wireless text-success" }'>
                                                    <div class="tree-row">
                                                        <div class="tree-cell component-name fw-bold text-dark">{{ module.label }}</div>
                                                        <div class="tree-cell component-type">{{ $t('common.relayModule') }}</div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton :path="module.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex flex-wrap justify-content-center align-items-center gap-2">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
                    <button v-if="reader.is_uz_reader || reader.is_aperio_reader" id="meca-delete-reader" type="button" class="btn btn-danger"
                        :disabled="deletingReader" @click="confirmDeleteModal.show()">{{ $t('componentEditReader.deleteReader') }}</button>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
    </div>
    <ReaderFormatModal ref="readerFormatModal" @onReaderFormatUpdated="onReaderFormatUpdated" />
    <ColorPickerModal ref="colorPickerModal" @colorSelected="setLedColor" />
    <CardProfileListModal
        :profiles="cardProfiles"
        :can-add="canAddCardProfile"
        @add="addCardProfile"
        @edit="editCardProfile"
        @remove="removeCardProfile"
    />
    <CardProfileModal ref="cardProfileModal" @cardProfileSaved="saveCardProfile" />
    <WhiteCardModal ref="whiteCardModal" />
    <AperioLockModal ref="aperioLockModal" />
    <ConfirmationModal ref="confirmDeleteModal" :title="$t('common.confirmDelete')" :question="deleteReaderQuestion" type="danger"
        :button1="$t('common.cancel')" :button2="$t('common.delete')" :action="deleteMecaReader" />
</template>

<script>
import { readerTypes } from '@/constants/constants';
import { computed, reactive, ref } from 'vue';
import { useClientStore } from '@/stores/clientStore';
import { useUserStore } from '@/stores/userStore';
import { onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast'
import TempoWithUnit from '@/components/tempoWithUnit.vue';
import ExternalInput from '@/components/externalInput.vue';
import ExternalAlarmInput from '@/components/externalAlarmInput.vue';
import TreeButton from '@/components/treeButton';
import ReaderFormatModal from '@/modals/readerFormatModal.vue';
import ColorPickerModal from '@/modals/colorPickerModal.vue';
import CardProfileListModal from '@/modals/cardProfileListModal.vue';
import CardProfileModal from '@/modals/cardProfileModal.vue';
import WhiteCardModal from '@/modals/whiteCardModal.vue';
import AperioLockModal from '@/modals/aperioLockModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import { IPV4_REGEX, filterIpInput } from '@/utils/ipAddress';
import $ from 'jquery';

export default {
    name: 'ComponentEditHub',
    components: {
        TreeButton,
        ReaderFormatModal,
        ColorPickerModal,
        CardProfileListModal,
        CardProfileModal,
        WhiteCardModal,
        AperioLockModal,
        ConfirmationModal,
        ExternalInput,
        ExternalAlarmInput,
        TempoWithUnit
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();

        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const readerPosition = parseInt(route.query.position);

        const hub = userStore.getHub(facilityId, hubId);
        const storeReader = hub.readers.find(reader => reader.position === readerPosition);
        const reader = reactive(storeReader.clone());
        const readerFormatModal = ref(null);
        const colorPickerModal = ref(null);
        const cardProfileModal = ref(null);
        const externalAlarms = userStore.getExternalAlarms(facilityId) || [];
        // As in the app, the alarm is driven by an internal relay unless an external alarm system is chosen
        const usesAlarmRelay = computed(() => externalAlarms.length === 0 || reader.alarm_control.output.mode == 0);

        const available_lprs = ref([]);

        const ledFields = [
            { key: 'closed', label: t('componentEditReader.leds.closed') },
            { key: 'open', label: t('componentEditReader.leds.open') },
            { key: 'alarm_on', label: t('componentEditReader.leds.alarmOn') },
            { key: 'denied', label: t('componentEditReader.leds.denied') },
            { key: 'bluetooth', label: t('componentEditReader.leds.bluetooth') },
        ];

        const colorToHex = (value) => `#${Number(value ?? 0).toString(16).padStart(6, '0').slice(-6).toUpperCase()}`;

        const hasRgbLeds = computed(() => reader.reader_formats.some(format =>
            readerTypes.find(type => type.formatId === format.format_id)?.has_rgb_leds === true
        ));

        const openColorPicker = (field) => {
            colorPickerModal.value.open(
                field.key,
                field.label,
                colorToHex(reader.led_config[field.key]),
                hasRgbLeds.value
            );
        };

        const setLedColor = (key, value) => {
            reader.led_config[key] = value;
        };

        const availableReaderTypes = (format) => {
            const sortReaderTypes = (a, b) => {
                const aIsWAuto = a.formatType === "WAuto";
                const bIsWAuto = b.formatType === "WAuto";

                if (aIsWAuto && !bIsWAuto) return -1;
                if (!aIsWAuto && bIsWAuto) return 1;

                return a.label.localeCompare(b.label);
            };

            let formatIds = new Set(reader.reader_formats.map(format => format.format_id).filter(id => id != format));

            if(formatIds.size == 0) {
                return readerTypes.sort(sortReaderTypes);
            }

            let includes = readerTypes.filter(e => formatIds.has(e.formatId)).map(e => new Set(e.compatible)).reduce((acc, value, i) => {
                if (i == 0) {
                    return value;
                } else {
                    return acc.intersection(value);
                }
            }, new Set());

            return readerTypes.filter(readerType => includes.has(readerType.formatId)).sort(sortReaderTypes);
        }

        const inputs = computed(() => {
            const clientStore = useClientStore();
            return clientStore.getInputs();
        });

        const outputs = computed(() => {
            const clientStore = useClientStore();
            return clientStore.getOutputs();
        });

        const canAddReaderFormat = computed(() => {
            return availableReaderTypes().length > 0 && reader.reader_formats.length < 5;
        });

        const cardProfiles = computed(() => {
            return userStore.getCardProfiles(facilityId);
        });

        const availableCardProfileTypes = (editedProfile = null) => {
            if(editedProfile) {
                return [editedProfile.type];
            }

            const existingTypes = cardProfiles.value.map(profile => profile.type);
            let types = ['HAXO', 'MIFARE', 'DESFIRE', 'STID_VCARD'];
            if(existingTypes.includes('HAXO')) {
                types = types.filter(type => !['HAXO', 'DESFIRE'].includes(type));
            }
            if(existingTypes.includes('MIFARE')) {
                types = types.filter(type => type !== 'MIFARE');
            }
            if(existingTypes.includes('DESFIRE')) {
                types = types.filter(type => !['DESFIRE', 'HAXO'].includes(type));
            }
            if(existingTypes.includes('STID_VCARD')) {
                types = types.filter(type => type !== 'STID_VCARD');
            }
            return types;
        };

        const canAddCardProfile = computed(() => {
            return cardProfiles.value.length < 3 && availableCardProfileTypes().length > 0;
        });

        const addReaderFormat = () => {
            readerFormatModal.value.setFormat(-1, null, availableReaderTypes(), getUsedBusAddresses());
        };

        const editReaderFormat = (format, index) => {
            const availableTypes = availableReaderTypes(format.format_id);
            readerFormatModal.value.setFormat(index, format, availableTypes, getUsedBusAddresses(index));
        };

        const getUsedBusAddresses = (editedFormatIndex = -1) => {
            return reader.reader_formats.filter((format, index) => format.is_rs485 && index !== editedFormatIndex).map(format => Number(format.bus_address));
        }
        
        const removeReaderFormat = (index) => {
            reader.reader_formats.splice(index, 1);
        };

        const onReaderFormatUpdated = (index, format) => {
            if(index == -1) {
                reader.reader_formats.push(format);
            } else {
                reader.reader_formats[index] = format;
            }
        };

        const addCardProfile = () => {
            cardProfileModal.value.setProfile(null, cardProfiles.value);
        };

        const editCardProfile = (profile) => {
            cardProfileModal.value.setProfile(profile, cardProfiles.value);
        };

        const saveCardProfile = async (profile) => {
            try {
                if(profile.id === 0) {
                    await userStore.createCardProfile(facilityId, profile);
                    showToast('success', t('componentEditReader.formatAdded'));
                } else {
                    await userStore.updateCardProfile(facilityId, profile);
                    showToast('success', t('componentEditReader.formatUpdated'));
                }
            } catch(error) {
                showToast('error', error);
            }
        };

        const removeCardProfile = async (profile) => {
            if(!confirm(t('componentEditReader.confirmDeleteFormat'))) return;
            try {
                await userStore.deleteCardProfile(facilityId, profile);
                showToast('success', t('componentEditReader.formatDeleted'));
            } catch(error) {
                showToast('error', error);
            }
        };

        // Mechatronic readers, as in the app: white list, lock of an Aperio reader, hub of a UZ or Aperio reader
        const whiteCardModal = ref(null);
        const aperioLockModal = ref(null);
        const confirmDeleteModal = ref(null);
        const changingLock = ref(false);
        const deletingReader = ref(false);
        const originalGateway = storeReader.meca_config
            ? { name: storeReader.meca_config.gateway_name, ip: storeReader.meca_config.gateway_ip }
            : null;

        const whiteList = computed(() => (reader.meca_config && reader.meca_config.white_list) || []);

        const addWhiteCard = async () => {
            const number = await whiteCardModal.value.show();
            if (number == null) return;
            if (!reader.meca_config.white_list) reader.meca_config.white_list = [];
            if (!reader.meca_config.white_list.includes(number)) reader.meca_config.white_list.push(number);
        };

        const removeWhiteCard = (number) => {
            reader.meca_config.white_list = reader.meca_config.white_list.filter(n => n !== number);
        };

        const onGatewayNameInput = (event) => {
            event.target.setCustomValidity('');
        };

        const onGatewayIpInput = (event) => {
            const value = filterIpInput(event.target.value);
            event.target.value = value;
            event.target.setCustomValidity(value === '' || IPV4_REGEX.test(value) ? '' : t('componentEditReader.checkInput'));
            reader.meca_config.gateway_ip = value;
        };

        // The device group id is a decimal or hexadecimal address, as in the app
        const parseDeviceGroupId = (id) => {
            if (/^\d+$/.test(id)) return parseInt(id, 10);
            if (/^[0-9a-fA-F]+$/.test(id)) return parseInt(id, 16);
            return 0;
        };

        // Moves the reader to another lock of its Aperio hub, the locks of other readers excluded
        const changeAperioLock = async () => {
            changingLock.value = true;
            let locks;
            try {
                locks = await userStore.discoverAperioLocks(facilityId, hub, reader.meca_config.gateway_ip.trim());
            } catch (message) {
                showToast('error', message);
                return;
            } finally {
                changingLock.value = false;
            }

            const meca = reader.meca_config;
            const isCurrentLock = (lock) => lock.device_group_id === String(meca.product_address) || (lock.mac != null && lock.mac === meca.product_serial);
            const declared = hub.getDeclaredAperioLocks();
            const available = locks.filter(lock => isCurrentLock(lock) || (!declared.has(lock.device_group_id) && !(lock.mac != null && declared.has(lock.mac))));
            if (available.length === 0) {
                showToast('error', t('meca.noLockFound'));
                return;
            }
            // The current lock first
            const current = available.find(isCurrentLock) ?? null;
            if (current) available.unshift(...available.splice(available.indexOf(current), 1));

            const selected = await aperioLockModal.value.show(available, current);
            if (!selected) return;
            meca.product_address = parseDeviceGroupId(selected.device_group_id);
            meca.product_serial = selected.mac;
        };

        // The other readers of the same hub follow its new name and address, as in the app
        const propagateGatewayChange = () => {
            if (!(reader.is_uz_reader || reader.is_aperio_reader) || !originalGateway) return;
            const meca = reader.meca_config;
            if (meca.gateway_name === originalGateway.name && meca.gateway_ip === originalGateway.ip) return;
            for (const other of hub.readers) {
                const otherMeca = other.meca_config;
                if (other.position !== reader.position && otherMeca && otherMeca.provider === meca.provider && otherMeca.gateway_ip === originalGateway.ip) {
                    otherMeca.gateway_name = meca.gateway_name;
                    otherMeca.gateway_ip = meca.gateway_ip;
                }
            }
            originalGateway.name = meca.gateway_name;
            originalGateway.ip = meca.gateway_ip;
        };

        const deleteReaderQuestion = computed(() => `${t('componentEditReader.confirmDeleteReader')}<br>${t('componentEditReader.deleteWarning')}`);

        const deleteMecaReader = async () => {
            deletingReader.value = true;
            try {
                if (reader.is_uz_reader) {
                    await userStore.unpairUZReader(facilityId, hub, storeReader);
                } else {
                    await userStore.deleteAperioReader(facilityId, hub, storeReader);
                }
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('common.updateSuccess'));
            } catch (message) {
                showToast('error', message);
            } finally {
                deletingReader.value = false;
            }
        };

        const handleSubmit = async () => {
            if (reader.is_uz_reader || reader.is_aperio_reader) {
                // Trimmed as in the app: a blank hub name is refused
                reader.meca_config.gateway_name = reader.meca_config.gateway_name.trim();
                if (reader.meca_config.gateway_name === '') {
                    const input = document.getElementById('meca-hub-name');
                    input.setCustomValidity(t('componentEditReader.hubNamePlaceholder'));
                    input.reportValidity();
                    return;
                }
            }
            const success = await userStore.updateReader(facilityId, hubId, storeReader, reader);
            if (success) {
                propagateGatewayChange();
                showToast('success', t('common.updateSuccess'));
            } else {
                showToast('error', t('common.updateFailure'));
            }
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const isSelected = (position) => {
            return position === readerPosition ? true : undefined;
        };

        const getReaderIcon = (position) => {
            return JSON.stringify({
                icon: 'mdi mdi-key-wireless text-success',
                selected: isSelected(position)
            });
        };

        const tree = ref(null);

        onMounted(() => {
            $(tree.value).jstree();
            $(tree.value).on('click', '.edit-button', (event) => {
                const jsonURI = $(event.currentTarget).attr('data-url');
                const uri = JSON.parse(jsonURI);
                router.push({ path: uri.path, query: uri.params });
            });
            const clientStore = useClientStore();
            clientStore.loadOutputs(facilityId, hubId);
            clientStore.loadInputs(facilityId, hubId);
            available_lprs.value = userStore.loadLPRs(facilityId, reader.lpr_source_id);
        });

        return {
            tree,
            facilityId,
            hub,
            reader,
            handleSubmit,
            isSelected,
            getReaderIcon,
            pathToInstallationEditComponents,
            readerFormatModal,
            colorPickerModal,
            cardProfileModal,
            addReaderFormat,
            editReaderFormat,
            removeReaderFormat,
            onReaderFormatUpdated,
            canAddReaderFormat,
            cardProfiles,
            canAddCardProfile,
            addCardProfile,
            editCardProfile,
            saveCardProfile,
            removeCardProfile,
            outputs,
            inputs,
            externalAlarms,
            usesAlarmRelay,
            whiteCardModal,
            aperioLockModal,
            confirmDeleteModal,
            changingLock,
            deletingReader,
            whiteList,
            addWhiteCard,
            removeWhiteCard,
            onGatewayNameInput,
            onGatewayIpInput,
            changeAperioLock,
            deleteReaderQuestion,
            deleteMecaReader,
            available_lprs,
            ledFields,
            colorToHex,
            setLedColor,
            openColorPicker,
        };
    },
};
</script>

<style scoped>
.color-swatch {
    align-items: center;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 0.25rem 0 0 0.25rem;
    box-shadow: inset 0 1px 1px rgba(49, 58, 70, 0.06);
    display: inline-flex;
    flex: 0 0 58px;
    height: 38px;
    justify-content: center;
    padding: 4px;
    width: 58px;
}

.color-swatch span {
    border: 1px solid rgba(49, 58, 70, 0.22);
    border-radius: 0.18rem;
    box-shadow: 0 1px 2px rgba(49, 58, 70, 0.16);
    display: block;
    height: 100%;
    width: 100%;
}

.color-swatch:focus-visible {
    box-shadow: 0 0 0 0.15rem rgba(97, 78, 222, 0.25);
    outline: 0;
}

.buzzer-select {
    max-width: 155px;
}

.form-actions-sticky {
    background-color: #fafbfe;
    z-index: 20;
}
</style>
